import { WORKSPACE_LOCATION_FILTER_FIELDS } from './workspaceFiltersModel';
import type {
  VideoAnalyticType,
  WorkspaceFilters,
  WorkspaceFilterValue,
} from './workspaceFiltersModel';

interface DetectionAndMatchFilterActivity {
  age: boolean;
  apparentAge: boolean;
  apparentGender: boolean;
  backpack: boolean;
  gender: boolean;
  handlers: boolean;
  headwear: boolean;
  lists: boolean;
  location: boolean;
  lower: boolean;
  shoes: boolean;
  similarity: boolean;
  sources: boolean;
  upper: boolean;
  bodyCount: number;
  commonCount: number;
  faceCount: number;
  totalCount: number;
}

interface VideoAnalyticsScoreField {
  filterName: string;
  key?: string;
}

interface VideoAnalyticsFilterActivityOptions {
  analyticType: VideoAnalyticType;
  facecoverVersion?: string;
  scoreField?: VideoAnalyticsScoreField;
  scoreFields?: VideoAnalyticsScoreField[];
}

interface VideoAnalyticsFilterActivity {
  location: boolean;
  score: boolean;
  streams: boolean;
  totalCount: number;
}

function hasArrayValues(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function hasLocationValues(filters: Record<string, WorkspaceFilterValue>): boolean {
  return WORKSPACE_LOCATION_FILTER_FIELDS.some((field) => {
    const value = filters[field];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

function countApplied(values: boolean[]): number {
  return values.filter(Boolean).length;
}

function getDetectionAndMatchFilterActivity(
  filters: WorkspaceFilters,
): DetectionAndMatchFilterActivity {
  const activity = {
    age: Boolean(filters.age),
    apparentAge: Boolean(filters.apparent_age),
    apparentGender: Boolean(filters.apparent_gender),
    backpack: hasArrayValues(filters.backpack_states),
    gender: Boolean(filters.gender),
    handlers: hasArrayValues(filters.handler_ids),
    headwear: [filters.headwear_apparent_colors, filters.headwear_states].some(hasArrayValues),
    lists: Boolean(filters.top_matching_candidates_label),
    location: hasLocationValues(filters),
    lower: [filters.lower_garment_colors, filters.lower_garment_types].some(hasArrayValues),
    shoes: hasArrayValues(filters.shoes_apparent_colors),
    similarity: [
      filters.top_similar_object_similarity__gte,
      filters.top_similar_object_similarity__lt,
    ].some(Boolean),
    sources: hasArrayValues(filters.sources),
    upper: [filters.upper_clothing_colors, filters.sleeve_lengths].some(hasArrayValues),
  };
  const commonCount = countApplied([
    activity.sources,
    activity.handlers,
    activity.similarity,
    activity.location,
    activity.lists,
  ]);
  const faceCount = countApplied([activity.gender, activity.age]);
  const bodyCount = countApplied([
    activity.apparentGender,
    activity.apparentAge,
    activity.lower,
    activity.upper,
    activity.headwear,
    activity.shoes,
    activity.backpack,
  ]);

  return {
    ...activity,
    bodyCount,
    commonCount,
    faceCount,
    totalCount: commonCount + faceCount + bodyCount,
  };
}

function getVideoAnalyticsFilterActivity(
  filters: WorkspaceFilters,
  {
    analyticType,
    facecoverVersion = undefined,
    scoreField = undefined,
    scoreFields = undefined,
  }: VideoAnalyticsFilterActivityOptions,
): VideoAnalyticsFilterActivity {
  const analyticFilters = filters.videoAnalytics[analyticType]?.filters ?? {};
  const isFacecoverV2 = analyticType === 'facecover' && facecoverVersion === 'v2';
  const { subtype } = analyticFilters;
  const activeScoreField = isFacecoverV2
    ? scoreFields?.find(({ key }) => key === subtype)
    : scoreField;
  const scoreValue = activeScoreField
    ? analyticFilters[activeScoreField.filterName]
    : undefined;
  const activity = {
    location: hasLocationValues(analyticFilters),
    score: Boolean(scoreValue) && (!isFacecoverV2 || Boolean(subtype)),
    streams: hasArrayValues(analyticFilters.stream_ids),
  };

  return {
    ...activity,
    totalCount: countApplied(Object.values(activity)),
  };
}

export {
  getDetectionAndMatchFilterActivity,
  getVideoAnalyticsFilterActivity,
};

export type {
  DetectionAndMatchFilterActivity,
  VideoAnalyticsFilterActivity,
  VideoAnalyticsFilterActivityOptions,
  VideoAnalyticsScoreField,
};
